/**
 * Where the API lives — decided in ONE place.
 *
 * WHAT WENT WRONG, 2026-09-10. Eight service files each carried their own copy of
 * `process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787'`. A production frontend was then
 * rebuilt without that variable set, so every one of them fell back to LOCALHOST — on a live site,
 * where localhost is the visitor's own machine and nothing is listening. Login, saving, the
 * calculation engine and the beta gate were all pointing at a machine that isn't the server.
 *
 * The beta gate happened to be the first thing on the page that calls the API, so it was the first
 * to fail, and it failed visibly — which made it look like the gate's bug. It was not: the gate
 * caught a deploy in which nothing would have worked, and said so before anyone got far enough in to
 * be confused by it.
 *
 * TWO CHANGES, so this cannot repeat:
 *
 *   ONE COPY. Eight identical fallbacks is how one gets corrected and seven do not. Everything now
 *   resolves through here.
 *
 *   A SANE FALLBACK. Defaulting to localhost is right in development and never right anywhere else.
 *   When the page is served from a real domain and no address was configured, the API is assumed to
 *   be on the SAME ORIGIN as the app — which is how this is actually deployed. A missing build
 *   variable can now cost, at worst, a same-origin guess that is very likely correct, instead of
 *   silently aiming the whole application at the visitor's own computer.
 *
 * Setting REACT_APP_API_BASE_URL at build time still wins, and is still the right thing to do when
 * the API is on a different host from the site.
 */

/** True for the addresses a developer's own machine answers on. */
function isLocalHostname(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
}

/**
 * The base URL every API call is built from. Never ends in a slash.
 *
 * Order: the build-time setting, then the page's own origin (real domains), then localhost (dev).
 */
export function apiBaseUrl(): string {
    const configured = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    if (configured) return configured.replace(/\/+$/, '');

    if (typeof window !== 'undefined' && window.location) {
        const { hostname, origin } = window.location;
        // A real domain: the API sits alongside the app unless we were told otherwise.
        if (hostname && !isLocalHostname(hostname) && origin) return origin.replace(/\/+$/, '');
    }

    return 'http://localhost:8787';
}
